import React, { useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "./ui/sheet";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Trash, Plus, Minus, ShoppingBag } from "@phosphor-icons/react";
import { useCart } from "../lib/cart";
import { createCheckoutSession } from "../lib/api";
import { toast } from "sonner";

export const CartSheet = () => {
  const { items, isOpen, setIsOpen, subtotal, updateQty, removeItem } =
    useCart();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const handleCheckout = async () => {
    if (items.length === 0) return;
    setLoading(true);
    try {
      const origin = window.location.origin;
      const payload = items.map((i) => ({
        product_id: i.product_id,
        quantity: i.quantity,
      }));
      const res = await createCheckoutSession(payload, origin, email || null);
      if (res.url) {
        window.location.href = res.url;
      } else {
        toast.error("Failed to start checkout");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetContent
        className="rounded-none border-l-2 border-zinc-950 w-full sm:max-w-md flex flex-col"
        data-testid="cart-sheet"
      >
        <SheetHeader className="border-b border-zinc-200 pb-4">
          <SheetTitle className="font-heading font-black text-2xl tracking-tight flex items-center gap-2">
            <ShoppingBag size={24} weight="duotone" />
            YOUR CART
          </SheetTitle>
          <SheetDescription className="font-mono uppercase text-xs tracking-[0.2em]">
            {items.length} {items.length === 1 ? "item" : "items"}
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto py-4 -mx-6 px-6">
          {items.length === 0 ? (
            <div className="text-center py-16 text-zinc-500" data-testid="cart-empty-state">
              <ShoppingBag size={48} weight="duotone" className="mx-auto mb-3 text-zinc-300" />
              <p className="font-mono uppercase text-xs tracking-[0.2em]">
                Your cart is empty
              </p>
            </div>
          ) : (
            <ul className="space-y-4">
              {items.map((item) => (
                <li
                  key={item.product_id}
                  className="border border-zinc-200 p-3 flex gap-3"
                  data-testid={`cart-item-${item.product_id}`}
                >
                  <div className="w-20 h-20 bg-zinc-50 border border-zinc-200 flex-shrink-0">
                    <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-heading font-semibold text-sm line-clamp-2 leading-snug">
                      {item.name}
                    </h4>
                    <div className="font-mono text-sm font-bold mt-1">
                      ${item.price.toFixed(2)}
                    </div>
                    <div className="flex items-center gap-2 mt-2">
                      <button
                        onClick={() => updateQty(item.product_id, item.quantity - 1)}
                        data-testid={`cart-decrease-${item.product_id}`}
                        className="w-7 h-7 border border-zinc-300 flex items-center justify-center hover:bg-zinc-100"
                      >
                        <Minus size={12} weight="bold" />
                      </button>
                      <span className="font-mono text-sm w-6 text-center">{item.quantity}</span>
                      <button
                        onClick={() => updateQty(item.product_id, item.quantity + 1)}
                        data-testid={`cart-increase-${item.product_id}`}
                        className="w-7 h-7 border border-zinc-300 flex items-center justify-center hover:bg-zinc-100"
                      >
                        <Plus size={12} weight="bold" />
                      </button>
                      <button
                        onClick={() => removeItem(item.product_id)}
                        data-testid={`cart-remove-${item.product_id}`}
                        className="ml-auto w-7 h-7 text-red-600 hover:bg-red-50 flex items-center justify-center"
                      >
                        <Trash size={14} weight="bold" />
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {items.length > 0 && (
          <div className="border-t-2 border-zinc-950 pt-4 space-y-4">
            <Input
              type="email"
              placeholder="Email (optional)"
              data-testid="checkout-email-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="rounded-none font-mono text-sm"
            />
            <div className="flex justify-between items-center">
              <span className="font-mono uppercase text-xs tracking-[0.2em]">Subtotal</span>
              <span className="font-mono text-2xl font-bold" data-testid="cart-subtotal">
                ${subtotal.toFixed(2)}
              </span>
            </div>
            <Button
              onClick={handleCheckout}
              disabled={loading}
              data-testid="checkout-button"
              className="w-full rounded-none bg-zinc-950 hover:bg-[#002FA7] text-white font-bold uppercase tracking-wider h-12"
            >
              {loading ? "Processing..." : "Checkout with Stripe"}
            </Button>
            <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-zinc-400 text-center">
              Secure payment · Test mode
            </p>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};
